import { useState } from 'react';

import Video from '@cartwhl/ui/atoms/Video';
import Text from '@cartwhl/ui/base/Text';
import useViewport from '@cartwhl/ui/hooks/useViewport';

import { CDN } from 'consts';

import FadeHeadline from './FadeHeadline';
import TypewriterHeadline from './TypewriterHeadline';

const heroTabs = [
  { label: 'for games', videoUrl: `${CDN}v2_homepage_hero_placeholder.webm` },
  {
    label: 'for ads & social',
    videoUrl: `${CDN}v2_homepage_hero_placeholder.webm`,
  },
  {
    label: 'for film & tv',
    videoUrl: `${CDN}v2_homepage_hero_placeholder.webm`,
  },
  {
    label: 'for the real world',
    videoUrl: `${CDN}v2_homepage_hero_placeholder.webm`,
  },
];

const heroHeadlines = ['Accelerate the\n art and science\n of motion'];
const HERO_HEADLINE_ANIMATION: 'typewriter' | 'fade' = 'typewriter' as const;
const HERO_HEADLINE_CURSOR_VARIANT: 'thin' | 'thick' = 'thick' as const;

export default function HeroSection() {
  const [activeTab] = useState(0);

  const { height: viewportHeight, isLg } = useViewport();

  return (
    <section>
      <div className="px-3 md:px-8">
        <div className="group relative mx-auto max-w-[1984px]">
          <div className="relative overflow-hidden rounded-2xl lg:rounded-3xl">
            <Video
              key={activeTab}
              src={heroTabs[activeTab].videoUrl}
              mode="autoplay"
              className="absolute inset-0 h-full w-full"
            />
            <div
              className="pointer-events-none absolute inset-0"
              style={{
                background:
                  'radial-gradient(circle,rgba(0, 0, 0, 0) 0%, rgba(22, 22, 22, 0.48) 100%)',
              }}
            >
              <div className="container pt-4 lg:pt-6 xl:pt-10">
                {HERO_HEADLINE_ANIMATION === 'typewriter' ? (
                  <TypewriterHeadline
                    headlines={heroHeadlines}
                    cursorVariant={HERO_HEADLINE_CURSOR_VARIANT}
                  />
                ) : (
                  <FadeHeadline headlines={heroHeadlines} />
                )}
              </div>
            </div>
            <div
              className="aspect-394/208 lg:aspect-video lg:max-h-[1160px] lg:min-h-[500px]"
              style={{
                maxHeight: isLg ? `${viewportHeight - 128 - 32}px` : undefined,
              }}
            />

            {/* Controls overlay - always on video */}
            <div
              className="absolute inset-x-0 bottom-4 flex justify-center lg:bottom-9"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="container flex items-center gap-4 lg:gap-8">
                {heroTabs.map((tab) => (
                  <button
                    key={tab.label}
                    disabled
                    className="hidden cursor-default text-white transition-colors lg:block"
                  >
                    <Text variant="web/body/medium/24">{tab.label}</Text>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Hero Tabs - mobile only (outside video) */}
          <div
            className="relative py-4 lg:hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Left gradient fade */}
            <div className="dark:from-gray-90/40 pointer-events-none absolute top-0 left-0 z-10 h-full w-8 bg-linear-to-r from-white to-transparent" />
            {/* Right linear fade */}
            <div className="dark:from-gray-90/40 pointer-events-none absolute top-0 right-0 z-10 h-full w-8 bg-linear-to-l from-white to-transparent" />
            <div className="no-scrollbar overflow-x-auto">
              <div className="flex items-center gap-4 px-4">
                {heroTabs.map((tab) => (
                  <button
                    key={tab.label}
                    className="whitespace-nowrap transition-colors"
                  >
                    <Text
                      variant="web/body/medium/md"
                      className={'text-gray-90'}
                    >
                      {tab.label}
                    </Text>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
